import React, {
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect
} from 'react';
import { useRepliconNavigationContext } from 'modules/common/hooks/RepliconNavigationProvider';
import { useIsBreakpointDown } from 'modules/common/hooks/useBreakpoint';

type SidePanelContextValue = {
  menuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  pinned: boolean;
  togglePinned: () => void;
  isDocked: boolean;
};

const SidePanelContext = React.createContext<SidePanelContextValue | null>(
  null
);

export const useSidePanelContext = (): SidePanelContextValue => {
  const val = useContext(SidePanelContext);
  if (!val) {
    throw new Error(
      'useSidePanelContext must be called within a SidePanelContextProvider'
    );
  }

  return val;
};

type SidePanelContextProviderProps = {
  children: React.ReactNode;
};

export const SidePanelContextProvider: React.FC<SidePanelContextProviderProps> = ({
  children
}: SidePanelContextProviderProps) => {
  const navigationMetadata = useRepliconNavigationContext();
  const isPinned = navigationMetadata?.settings.some(
    (setting: { values: any[] }) =>
      setting.values.some((value) => value.text === 'pinned')
  );
  const isDocked = useIsBreakpointDown('md');
  const [pinned, setPinned] = useState(isPinned);
  const togglePinned = useCallback(() => setPinned(!pinned), [pinned]);
  const [menuOpen, setMenuOpen] = useState(isPinned);
  const openMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);
  useEffect(() => {
    setMenuOpen(pinned);
  }, [navigationMetadata.settings, pinned]);
  const value = useMemo(
    () => ({
      menuOpen,
      openMenu,
      closeMenu,
      pinned,
      togglePinned,
      isDocked
    }),
    [menuOpen, openMenu, closeMenu, pinned, togglePinned, isDocked]
  );

  return (
    <SidePanelContext.Provider value={value}>
      {children}
    </SidePanelContext.Provider>
  );
};

export default SidePanelContextProvider;
